import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, TextField } from '../components/ui';
import { apiClient, ApiClientError } from '../api/client';
import { fieldErrorsFromApiError } from '../lib/field-errors';
import { ContactDetails } from './ContactDetails';

interface EnquiryInput {
  name: string;
  phone: string;
  email?: string;
  company?: string;
  message?: string;
  planCode?: string;
  website?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(f: EnquiryInput): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!f.name.trim()) errors.name = 'Enter your name.';
  else if (f.name.trim().length > 120) errors.name = 'Name must be 120 characters or fewer.';
  if (!f.phone.trim()) errors.phone = 'Enter your phone number.';
  else if (f.phone.replace(/\D/g, '').length < 10) {
    errors.phone = 'Enter a phone number with at least 10 digits.';
  } else if (f.phone.trim().length > 30) errors.phone = 'Phone must be 30 characters or fewer.';
  if (f.email?.trim() && !EMAIL_RE.test(f.email.trim())) {
    errors.email = 'Enter a valid email address.';
  }
  if ((f.company ?? '').trim().length > 200) {
    errors.company = 'Company must be 200 characters or fewer.';
  }
  if ((f.message ?? '').trim().length > 2000) {
    errors.message = 'Message must be 2000 characters or fewer.';
  }
  return errors;
}

/**
 * Website enquiry form (no login) - becomes a lead and a call-back follow-up
 * for our team (POST /public/enquiries). \`website\` is a hidden honeypot.
 */
export function EnquiryForm({
  planCode,
  planName,
  onDone,
}: {
  planCode?: string;
  planName?: string;
  /** Called after a successful send (e.g. to close a popup). */
  onDone?: () => void;
}) {
  const [form, setForm] = useState<EnquiryInput>({
    name: '',
    phone: '',
    email: '',
    company: '',
    message: '',
    website: '',
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const send = useMutation<{ received: true }, ApiClientError, EnquiryInput>({
    mutationFn: (input) => apiClient.post('/public/enquiries', input),
  });

  const set = (key: keyof EnquiryInput) => (e: { target: { value: string } }) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setFieldErrors((f) => {
      if (!f[key]) return f;
      const { [key]: _removed, ...rest } = f;
      return rest;
    });
  };

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const errors = validate(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      return;
    }
    const trimmed = (v?: string) => v?.trim() || undefined;
    try {
      await send.mutateAsync({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: trimmed(form.email),
        company: trimmed(form.company),
        message: trimmed(form.message),
        planCode,
        website: form.website,
      });
      setSent(true);
      onDone?.();
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const server = fieldErrorsFromApiError(err);
        if (Object.keys(server).length > 0) {
          setFieldErrors(server);
          setError('Please fix the highlighted fields.');
          return;
        }
      }
      setError(
        err instanceof ApiClientError && err.httpStatus === 429
          ? 'Too many enquiries just now - please try again in a minute, or call us.'
          : err instanceof ApiClientError && err.httpStatus < 500
            ? err.message
            : 'Could not send your enquiry. Please call or email us instead.',
      );
    }
  }

  if (sent) {
    return (
      <div role="status" style={{ display: 'grid', gap: 'var(--space-2)' }}>
        <strong>Thank you - we have your enquiry.</strong>
        <span>Our team will call you back shortly. To talk now:</span>
        <ContactDetails />
      </div>
    );
  }

  return (
    <form className="ne-auth__form" onSubmit={(e) => void onSubmit(e)} noValidate>
      {planName ? (
        <p style={{ margin: 0 }}>
          About the <strong>{planName}</strong> plan
        </p>
      ) : null}
      <TextField
        label="Your name"
        required
        autoComplete="name"
        value={form.name}
        onChange={set('name')}
        error={fieldErrors.name}
      />
      <TextField
        label="Phone (WhatsApp)"
        required
        inputMode="tel"
        autoComplete="tel"
        placeholder="e.g. +91 98765 43210"
        value={form.phone}
        onChange={set('phone')}
        error={fieldErrors.phone}
      />
      <TextField
        label="Email"
        type="email"
        autoComplete="email"
        value={form.email}
        onChange={set('email')}
        error={fieldErrors.email}
      />
      <TextField
        label="Business name"
        autoComplete="organization"
        value={form.company}
        onChange={set('company')}
        error={fieldErrors.company}
      />
      <div className="ne-field">
        <label className="ne-field__label" htmlFor="enquiry-message">
          How can we help?
        </label>
        <textarea
          id="enquiry-message"
          className={['ne-input', fieldErrors.message ? 'ne-input--error' : '']
            .filter(Boolean)
            .join(' ')}
          rows={4}
          value={form.message}
          aria-invalid={fieldErrors.message ? true : undefined}
          onChange={set('message')}
        />
        {fieldErrors.message ? (
          <span className="ne-field__error" role="alert">
            {fieldErrors.message}
          </span>
        ) : null}
      </div>
      {/* Honeypot: hidden from people; bots fill it in. */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={form.website}
        onChange={set('website')}
        style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, opacity: 0 }}
      />
      {error ? (
        <p className="ne-auth__error" role="alert">
          {error}
        </p>
      ) : null}
      <Button type="submit" disabled={send.isPending}>
        {send.isPending ? 'Sending…' : 'Send enquiry'}
      </Button>
    </form>
  );
}
